import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../context/ThemeProvider.jsx'
// VIN-2026-64: the "You" tab is the UserMenu rendered in its bottom-sheet
// ('nav') placement. Contact + Wine of the Day fold into that menu; the
// top-right slot becomes a Notifications bell (PageHeader). VIN-2026-63:
// the centre FAB routes to /scan, so the old action-sheet (and its
// useState/useEffect + overlay-bus + CorkscrewGlyph imports) are gone.
import UserMenu from '../components/UserMenu.jsx'
import { useKeyboardOpen } from '../hooks/useKeyboardOpen.js'

// Brand-color constants kept for icon-default-color prop signatures (the
// SVG icon components default to gold). Anything that affects layout/
// background reads from the active theme inside BottomNav() below.
const C = { obsidian: '#0E0C12', cellar: '#3A0D18', burgundy: '#7B2040', gold: '#C4965A', ivory: '#F4EFE7' }

function BarrelIcon({ size = 22, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <ellipse cx="12" cy="5" rx="7" ry="2.5" stroke={color} strokeWidth="1.3" fill="none"/>
      <ellipse cx="12" cy="19" rx="7" ry="2.5" stroke={color} strokeWidth="1.3" fill="none"/>
      <line x1="5" y1="5" x2="5" y2="19" stroke={color} strokeWidth="1.3"/>
      <line x1="19" y1="5" x2="19" y2="19" stroke={color} strokeWidth="1.3"/>
      <path d="M5 9 C3.5 9 3 10.5 3 12 C3 13.5 3.5 15 5 15" stroke={color} strokeWidth="1.3" fill="none" strokeLinecap="round"/>
      <path d="M19 9 C20.5 9 21 10.5 21 12 C21 13.5 20.5 15 19 15" stroke={color} strokeWidth="1.3" fill="none" strokeLinecap="round"/>
      <path d="M5 9 Q12 7.5 19 9" stroke={color} strokeWidth="1.1" fill="none"/>
      <path d="M5 15 Q12 16.5 19 15" stroke={color} strokeWidth="1.1" fill="none"/>
      <circle cx="12" cy="12" r="1.2" fill={color} opacity="0.7"/>
    </svg>
  )
}

// VIN-2026-64: Discover tab icon — a compass. The tab leads to the
// community/discover hub (shops, hosts, public cellars, upcoming events);
// a compass reads as "explore" better than the prior people glyph.
function CompassIcon({ size = 22, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="9"/>
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>
    </svg>
  )
}

// VIN-2026-64: Tastings tab icon — a tasting glass (same silhouette as
// the WineDetail "Taste" affordance, so the verb reads consistently).
function TastingGlassIcon({ size = 22, color = '#C4965A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 2h8l-1 7c0 2-1 4-3 5v6h2v2H10v-2h2v-6c-2-1-3-3-3-5L8 2z"/>
    </svg>
  )
}

// VIN-2026-59: center-action icon — a clean search/scan magnifier.
// Replaces the corkscrew + "+" badge. Search-first framing: the
// primary verb of a wine app is "find / look up a bottle." The lens
// is deliberately empty (no grapes) so it reads as universal search,
// distinct from the grape-in-lens "Check a Wine" sheet item.
function MagnifierGlyph({ size = 26, stroke = '#F4EFE7' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="11" cy="11" r="7"/>
      <line x1="20" y1="20" x2="16.65" y2="16.65"/>
    </svg>
  )
}

function BottomNav({ user, pendingInvites = 0 }) {
  const navigate = useNavigate()
  const location = useLocation()
  const path = location.pathname
  // VIN-2026-73: collapse the nav while the soft keyboard is up so form
  // screens (Edit/Add Wine, etc.) get the vertical space back.
  const keyboardOpen = useKeyboardOpen()
  const { theme, themeMode } = useTheme()
  const isLight = themeMode === 'white'

  // VIN-2026-63 Ship D: the centre FAB now routes straight to the
  // unified /scan flow (camera → identify → adaptive result) instead of
  // opening an Add / Check / Uncork action sheet. The three old options
  // collapse into one scan whose RESULT adapts: a new bottle offers Add /
  // Taste / Bookmark; one already in your cellar leads with Uncork. The
  // sheet — and its overlay-bus broadcast, body-scroll lock, and pick
  // handlers — were removed with it.

  // VIN-2026-64 nav: Discover · Cellar · [Scan] · Tastings · You.
  const isCellar   = path === '/'
  const isDiscover = path.startsWith('/events') || path.startsWith('/event/')
  const isTastings = path.startsWith('/tastings')

  const btn = () => ({
    background: 'none', border: 'none', cursor: 'pointer',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    padding: '6px 8px', position: 'relative',
  })

  // Theme-aware nav colors. Light mode swaps to burgundy active +
  // charcoal-tinted inactive to match the parchment surface; dark mode
  // keeps the ivory-on-cellar pattern.
  //
  // VIN-007 — bumped light-mode inactive opacity 0.55 → 0.72 so
  // inactive tabs read as visible-but-secondary against ivory rather
  // than ghosting out completely. Dark mode unchanged (was fine).
  const inactiveColor = isLight ? 'rgba(42,31,37,0.72)' : 'rgba(244,239,231,0.78)'
  const activeColor   = isLight ? theme.colors.burgundy : theme.colors.gold

  const lbl = (active) => ({
    fontFamily: 'Raleway, sans-serif', fontSize: 10, fontWeight: 600,
    letterSpacing: '0.1em', textTransform: 'uppercase',
    color: active ? activeColor : inactiveColor,
  })

  // Nav background — iOS-style "frosted glass" treatment. Theme-aware
  // low-alpha gradient combined with a heavy backdrop-filter blur so
  // wine cards scrolling past underneath read through clearly. Tuned
  // lower in v2 because the previous 0.72–0.85 alpha read as nearly
  // opaque on mobile — the new range (light: 0.45–0.55, dark:
  // ~0.45–0.65) lets you obviously see content blurred through the
  // nav, which is the whole point of the glass treatment.
  const navBg = isLight
    ? 'linear-gradient(180deg, rgba(234,224,204,0.45) 0%, rgba(248,242,230,0.55) 100%)'
    : `linear-gradient(180deg, ${theme.colors.burgundy}80 0%, ${theme.colors.cellar}99 55%, ${theme.colors.obsidian}a8 100%)`
  // Gold accent border between the body and the bottom-nav in BOTH
  // themes. Mirrors PageHeader so the page is bracketed by matched
  // gold lines top + bottom, instead of the dark-mode bg fading into
  // the cellar-gradient nav with no defined break.
  const navBorder = isLight
    ? '2px solid rgba(196,150,90,0.5)'
    : '1px solid rgba(196,150,90,0.45)'

  return (
    <>
    <nav data-tour="bottom-nav" style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 40,
      background: navBg,
      // VIN-2026-59 (2026-05-27): heavier glass — blur 18→24,
      // saturate 140→160 — so cellar cards scrolling underneath read
      // as depth, not clutter. Pairs with the symmetric card padding
      // (VIN-2026-58) for a calmer scroll surface.
      backdropFilter: 'blur(24px) saturate(160%)',
      WebkitBackdropFilter: 'blur(24px) saturate(160%)',
      borderTop: navBorder,
      // VIN-2026-73: slide the nav off-screen while the keyboard is up.
      // translateY(150%) clears the centre FAB (it juts ~20px above the bar
      // via marginTop:-20); pointerEvents:none so a stray tap can't hit the
      // hidden nav mid-transition.
      transform: keyboardOpen ? 'translateY(150%)' : 'translateY(0)',
      opacity: keyboardOpen ? 0 : 1,
      pointerEvents: keyboardOpen ? 'none' : 'auto',
      transition: 'transform 0.25s ease, opacity 0.2s ease',
    }}>
      <div style={{ maxWidth: 500, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '8px 8px' }}>

        {/* Discover — the community hub (shops, hosts, public cellars,
            upcoming events) at /events. Carries the pending-invite badge
            (extend the communityNotifs aggregation in App.jsx as more
            sources come online). Renamed from "Social" + moved to the
            leading slot in the VIN-2026-64 IA restructure. */}
        <button onClick={() => navigate('/events')} style={btn(isDiscover)}>
          <CompassIcon size={22} color={isDiscover ? activeColor : inactiveColor} />
          <span style={lbl(isDiscover)}>Discover</span>
          {pendingInvites > 0 && (
            <div style={{
              position: 'absolute', top: 2, right: 4,
              width: 14, height: 14, borderRadius: '50%',
              background: C.burgundy, border: `1.5px solid ${theme.colors.bg}`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: 'Raleway, sans-serif', fontSize: 8, fontWeight: 700, color: 'white',
            }}>
              {pendingInvites}
            </div>
          )}
        </button>

        {/* Cellar */}
        <button onClick={() => navigate('/')} style={btn(isCellar)}>
          <BarrelIcon size={22} color={isCellar ? activeColor : inactiveColor} />
          <span style={lbl(isCellar)}>Cellar</span>
        </button>

        {/* Centre FAB — Scan. The magnifier opens the wine-action sheet
            (Add / Check / Uncork), all of which lead with scanning a
            label. Ship D will fold these into a single camera-first scan
            pipeline; the entry point stays here either way. */}
        <button data-tour="add-wine" aria-label="Scan a wine" onClick={() => navigate('/scan')} style={{ ...btn(false), marginTop: -20 }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg, #7B2040, #3A0D18)',
            border: '1px solid var(--app-border-emphasis)',
            boxShadow: '0 8px 24px rgba(123,32,64,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative',
          }}>
            <MagnifierGlyph size={26} stroke={C.ivory} />
          </div>
          <span style={{ ...lbl(false), color: isLight ? 'rgba(123,32,64,0.85)' : 'rgba(244,239,231,0.65)' }}>Scan</span>
        </button>

        {/* Tastings — the user's Deductive-Method tasting diary + event
            history at /tastings. New top-level tab in the VIN-2026-64
            restructure (was buried in the UserMenu). */}
        <button onClick={() => navigate('/tastings')} style={btn(isTastings)}>
          <TastingGlassIcon size={22} color={isTastings ? activeColor : inactiveColor} />
          <span style={lbl(isTastings)}>Tastings</span>
        </button>

        {/* You — the account menu (profile, mode switch, bookmarks,
            tickets, notifications, contact, sign out) as a slide-up
            bottom sheet. Replaces the top-right avatar dropdown, which
            ceded that slot to the Notifications bell. */}
        {user && <UserMenu user={user} connectionNotifs={pendingInvites} placement="nav" />}

      </div>
    </nav>
    </>
  )
}

export default BottomNav
